import { memo, useState } from 'react';
import {
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  Box,
  Text,
  Button,
  Flex,
  Icon,
} from '@chakra-ui/react';
import {
  //   MdLightbulb,
  MdHelp,
  MdDescription,
} from 'react-icons/md';

import LogoutModal from 'components/LogoutModal';
import BlocksIcon from 'components/Icons/BlocksIcon';
import DimensionsIcon from 'components/Icons/DimensionsIcon';
import ImportDataIcon from 'components/Icons/ImportDataIcon';
import ContactSupportIcon from 'components/Icons/ContactSupportIcon';
import DashboardIcon from 'components/Icons/DashboardIcon';
import DashboardIconActive from 'components/Icons/DashboardIconActive';

import { TabType } from '../types';
import { HEADER_CONFIG } from '../constants';

interface MobileNavigationMenuProperties {
  isOpen: boolean;
  onClose: () => void;
  activeTab: TabType;
  onTabChange: (tab: TabType) => void;
}

const MobileNavigationMenu = memo(
  ({ isOpen, onClose, activeTab, onTabChange }: MobileNavigationMenuProperties) => {
    const [logoutModal, setLogoutModal] = useState(false);

    const handleMenuItemClick = (tab: TabType) => {
      onTabChange(tab);
      onClose();
    };

    const handleHelpClick = () => {
      window.open('https://help.blox.so/', '_blank');
      onClose();
    };

    const handleContactClick = () => {
      window.open('https://www.blox.so/contact', '_blank');
      onClose();
    };

    const handleSignOut = () => {
      setLogoutModal(true);
      onClose();
    };

    return (
      <>
        <Drawer isOpen={isOpen} placement='right' onClose={onClose} size='sm'>
          <DrawerOverlay />
          <DrawerContent bg={HEADER_CONFIG.BG_COLOR}>
            <DrawerBody p={0}>
              <Box h='100%' display='flex' flexDirection='column'>
                {/* Navigation Section */}
                <Box p={6}>
                  <Text fontSize='sm' fontWeight='bold' color='black' mb={4} letterSpacing='wide'>
                    Navigation
                  </Text>

                  <Flex flexDirection='column' gap={2}>
                    {/* Dashboard */}
                    <Button
                      w='100%'
                      leftIcon={
                        <Box boxSize={5} display='flex' alignItems='center' justifyContent='center'>
                          {activeTab === 'dashboard' ? (
                            <DashboardIconActive width={20} height={20} />
                          ) : (
                            <DashboardIcon width={20} height={20} />
                          )}
                        </Box>
                      }
                      justifyContent='flex-start'
                      bg={activeTab === 'dashboard' ? '#745BE3' : 'transparent'}
                      color={activeTab === 'dashboard' ? 'white' : 'black'}
                      borderRadius='md'
                      py={3}
                      px={4}
                      _hover={{ bg: activeTab === 'dashboard' ? '#6347CC' : 'white' }}
                      onClick={() => handleMenuItemClick('dashboard')}
                    >
                      Dashboard
                    </Button>

                    {/* Blocks */}
                    <Button
                      w='100%'
                      leftIcon={
                        <Box
                          boxSize={5}
                          display='flex'
                          alignItems='center'
                          justifyContent='center'
                          filter={activeTab === 'blocks' ? 'brightness(0) invert(1)' : 'none'}
                        >
                          <BlocksIcon width={20} height={20} />
                        </Box>
                      }
                      justifyContent='flex-start'
                      bg={activeTab === 'blocks' ? '#745BE3' : 'transparent'}
                      color={activeTab === 'blocks' ? 'white' : 'black'}
                      borderRadius='md'
                      py={3}
                      px={4}
                      _hover={{ bg: activeTab === 'blocks' ? '#6347CC' : 'white' }}
                      onClick={() => handleMenuItemClick('blocks')}
                    >
                      Blocks
                    </Button>

                    {/* Dimensions */}
                    <Button
                      w='100%'
                      leftIcon={
                        <Box
                          boxSize={5}
                          display='flex'
                          alignItems='center'
                          justifyContent='center'
                          filter={activeTab === 'dimensions' ? 'brightness(0) invert(1)' : 'none'}
                        >
                          <DimensionsIcon width={20} height={20} />
                        </Box>
                      }
                      justifyContent='flex-start'
                      bg={activeTab === 'dimensions' ? '#745BE3' : 'transparent'}
                      color={activeTab === 'dimensions' ? 'white' : 'black'}
                      borderRadius='md'
                      py={3}
                      px={4}
                      _hover={{ bg: activeTab === 'dimensions' ? '#6347CC' : 'white' }}
                      onClick={() => handleMenuItemClick('dimensions')}
                    >
                      Dimensions
                    </Button>

                    {/* Import Data */}
                    <Button
                      w='100%'
                      leftIcon={
                        <Box
                          boxSize={5}
                          display='flex'
                          alignItems='center'
                          justifyContent='center'
                          filter={activeTab === 'import' ? 'brightness(0) invert(1)' : 'none'}
                        >
                          <ImportDataIcon width={20} height={20} />
                        </Box>
                      }
                      justifyContent='flex-start'
                      bg={activeTab === 'import' ? '#745BE3' : 'transparent'}
                      color={activeTab === 'import' ? 'white' : 'black'}
                      borderRadius='md'
                      py={3}
                      px={4}
                      _hover={{ bg: activeTab === 'import' ? '#6347CC' : 'white' }}
                      onClick={() => handleMenuItemClick('import')}
                    >
                      Import Data
                    </Button>
                  </Flex>
                </Box>

                {/* More Section */}
                <Box p={6} pt={0}>
                  <Text fontSize='sm' fontWeight='bold' color='black' mb={4} letterSpacing='wide'>
                    More
                  </Text>

                  <Flex flexDirection='column' gap={2}>
                    <Button
                      w='100%'
                      leftIcon={<Icon as={MdHelp} boxSize={5} />}
                      justifyContent='flex-start'
                      bg='transparent'
                      color='black'
                      borderRadius='md'
                      py={3}
                      px={4}
                      _hover={{ bg: 'white' }}
                      onClick={handleHelpClick}
                    >
                      Help Center
                    </Button>

                    <Button
                      w='100%'
                      leftIcon={<Icon as={MdDescription} boxSize={5} />}
                      justifyContent='flex-start'
                      bg='transparent'
                      color='black'
                      borderRadius='md'
                      py={3}
                      px={4}
                      _hover={{ bg: 'white' }}
                      onClick={onClose}
                    >
                      What's New
                    </Button>

                    <Button
                      w='100%'
                      leftIcon={
                        <Box boxSize={5} display='flex' alignItems='center' justifyContent='center'>
                          <ContactSupportIcon width={20} height={20} color='black' />
                        </Box>
                      }
                      justifyContent='flex-start'
                      bg='transparent'
                      color='black'
                      borderRadius='md'
                      py={3}
                      px={4}
                      _hover={{ bg: 'white' }}
                      onClick={handleContactClick}
                    >
                      Contact Support
                    </Button>
                  </Flex>
                </Box>

                {/* Sign out Button */}
                <Box p={6} pt={4} mt='auto'>
                  <Button
                    w='100%'
                    bg='white'
                    color='red.500'
                    border='1px solid'
                    borderColor='red.500'
                    borderRadius='md'
                    py={3}
                    _hover={{ bg: 'red.50' }}
                    onClick={handleSignOut}
                  >
                    Sign out
                  </Button>
                </Box>
              </Box>
            </DrawerBody>
          </DrawerContent>
        </Drawer>

        {logoutModal && (
          <LogoutModal
            isOpen={logoutModal}
            onClose={() => setLogoutModal(false)}
            onOpenAuthModal={() => {}}
          />
        )}
      </>
    );
  },
);

MobileNavigationMenu.displayName = 'MobileNavigationMenu';

export default MobileNavigationMenu;
